import React, { useId } from 'react'
import { Link } from 'react-router'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import {
  TopBar,
  AppLayout,
  Mtds,
  useGetPlanSummariesQuery,
  getPlanById,
  getPlans,
  useAuth,
  useAllSamples,
  useGetSampleTakingReportQuery,
  type CompactPlan,
  type PlanSummary,
  type Sample,
  ExportIcon,
  TestTubeIcon,
  FileTextIcon,
  FileMinusIcon,
  useGetOwnerNameQueries,
  OwnerId,
  useGetOrganizationUnitsQuery,
  type Region,
  type Avdeling,
  type Seksjon,
  type Team,
  ScreenHeader
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { CumulativeStatusCard } from '../components/statistics/CumulativeStatusCard'
import { CumulativeChart } from '../components/statistics/CumulativeChart'
import {
  buildPaddedChartData,
  padChartColumns,
  useHideChartSpacerBars,
  useChartBarValueLabels,
  type ChartElementRef
} from '../components/statistics/statisticsChartUtils'
import { StatisticsFilters } from '../components/statistics/statisticsFilters'
import {
  getSummaryMetricTitle,
  SummaryMetricCardItems
} from '../components/statistics/SummaryMetricCards'
import { ALL_FILTER_VALUE } from '../components/statistics/statisticsFilterUtils'
import {
  formatCount,
  formatPercent
} from '../components/statistics/statisticsFormat'
import type {
  CumulativeChartRow,
  FilterOption
} from '../components/statistics/statisticsTypes'
import {
  buildCumulativeCalendarSeries,
  getSubsampleResultMetrics,
  getOsloYearDateRange,
  isDateInOsloYear,
  sortEftaCodes,
  buildStatusAtEndOfPreviousMonth,
  DelegationOwnerType,
  OwnerBreakdownRow,
  PlanBreakdownRow,
  getStatisticsYears,
  getOwnerTypeLabel,
  compareByRemainingThenSize,
  mapArrayToSamplesDurationStatusAvdeling,
  filterExpiredOrganizationUnits
} from '../utils/statisticsUtils'
import { useStatusDurationReport } from '../hooks/useDashboard'
import { useEftaNumberOptions } from '../hooks/useEftaNumberOptions'
import styles from './StatisticsScreen.module.css'
import {
  shapePlansForStatisticsExport,
  shapeSampleForStatisticsExport,
  shapeSelectedPlanForExport
} from '../utils/statisticsExportUtils'

const hasCumulativeData = <T extends { id: number; month: string }>(
  rows: T[]
) =>
  rows.some((row) =>
    Object.entries(row).some(
      ([key, value]) =>
        !['id', 'month', 'date', 'label'].includes(key) && Number(value) > 0
    )
  )

const getYearFromDate = (date?: string) =>
  date == null ? undefined : new Date(date).getUTCFullYear()

const getSamplesForYear = (samples: Sample[] | undefined, year: number) =>
  samples?.filter((sample) => isDateInOsloYear(sample.takenDate, year)) ?? []

const ownerIdMatchesFilter = (ownerId: string, allowedOwnerIds?: Set<string>) =>
  !allowedOwnerIds || allowedOwnerIds.has(ownerId)

const planMatchesOwnerFilter = (
  plan: CompactPlan,
  allowedOwnerIds?: Set<string>
): boolean =>
  !allowedOwnerIds ||
  plan.tasks?.some((task) =>
    task.delegations?.some((delegation) =>
      ownerIdMatchesFilter(delegation.ownerId, allowedOwnerIds)
    )
  ) ||
  false

const filterPlansForExport = ({
  plans,
  year,
  selectedPlan,
  allowedOwnerIds
}: {
  plans: CompactPlan[] | undefined
  year: number
  selectedPlan: number | undefined
  allowedOwnerIds?: Set<string>
}) =>
  (plans ?? []).filter((plan) => {
    if (selectedPlan !== undefined && plan.id !== selectedPlan) return false
    if (selectedPlan === undefined && getYearFromDate(plan.due) !== year) {
      return false
    }

    return planMatchesOwnerFilter(plan, allowedOwnerIds)
  })

const trimChar = (str: string, char: string): string => {
  let start = 0
  let end = str.length
  while (start < end && str[start] === char) start++
  while (end > start && str[end - 1] === char) end--
  return str.slice(start, end)
}

const getFilenameSafePart = (value: string) =>
  trimChar(
    value
      .trim()
      .toLowerCase()
      .replaceAll('æ', 'ae')
      .replaceAll('ø', 'o')
      .replaceAll('å', 'a')
      .replaceAll(/[^a-z0-9]+/g, '-'),
    '-'
  )

const getStatusMonthIndex = (year: number) => {
  const now = new Date()
  if (year < now.getUTCFullYear()) return 11
  if (year > now.getUTCFullYear()) return 0
  return Math.max(now.getUTCMonth() - 1, 0)
}

const getCurrentMonthIndexForYear = (year: number) => {
  const now = new Date()
  return year === now.getUTCFullYear() ? now.getUTCMonth() : 11
}

const safeOrgName = (name: string | null | undefined, fallback: string) => {
  return name && name.trim().length > 0 ? name : fallback
}

function sliceId(id: string): string {
  const sliced = id.slice(0, 10)
  return sliced + '...'
}

// Stages that carry a measurable duration for a sample's processing time.
// "Completed" is excluded because it is the terminal state: a sample's final
// status has no next event, so its duration is never measured. "Compromised"
// is included because it is a resolvable branch (a sample can be compromised
// and then continue to Completed), so it has a following transition and a real
// measured duration.
const SAMPLE_DURATION_PIPELINE_STAGES = [
  'sampleTaken',
  'sampleSent',
  'sampleReceived',
  'analysisStarted',
  'readyToProcess',
  'compromised'
] as const

const getSampleDurationLeadTime = (row: {
  sampleTaken: number
  sampleSent: number
  sampleReceived: number
  analysisStarted: number
  readyToProcess: number
  compromised: number
}) =>
  SAMPLE_DURATION_PIPELINE_STAGES.reduce((sum, stage) => sum + row[stage], 0)

const formatDays = (value: number) =>
  `${value.toLocaleString('nb-NO', { maximumFractionDigits: 1 })} dager`

// --- Helper functions to reduce cognitive complexity ---

const computeSelectedOwner = (
  teamId: string,
  seksjonId: string,
  avdelingId: string,
  regionId: string
): OwnerId | undefined => {
  if (isSelected(teamId)) return new OwnerId('team', teamId)
  if (isSelected(seksjonId)) return new OwnerId('seksjon', seksjonId)
  if (isSelected(avdelingId)) return new OwnerId('avdeling', avdelingId)
  if (isSelected(regionId)) return new OwnerId('region', regionId)
  return undefined
}

const computeSelectedPlanOwnerIds = (
  teamId: string,
  seksjonId: string,
  avdelingId: string,
  regionId: string,
  regionAvdelinger: Avdeling[]
): Set<string> | undefined => {
  if (isSelected(teamId)) {
    return new Set([`team:${teamId}`])
  }
  if (isSelected(seksjonId)) {
    return new Set([`seksjon:${seksjonId}`])
  }
  if (isSelected(avdelingId)) {
    return new Set([`avdeling:${avdelingId}`])
  }
  if (isSelected(regionId)) {
    return new Set(regionAvdelinger.map((a) => `avdeling:${a.id}`))
  }
  return undefined
}

type OrgFilterSelection = {
  teamId: string
  seksjonId: string
  avdelingId: string
  regionId: string
}

type OrgFilterOptions = {
  teamOptions: FilterOption[]
  seksjonOptions: FilterOption[]
  avdelingOptions: FilterOption[]
  regionOptions: FilterOption[]
}

const isSelected = (id: string) => id !== ALL_FILTER_VALUE

const findSelectedOrgFilterLabel = ({
  selection,
  options
}: {
  selection: OrgFilterSelection
  options: OrgFilterOptions
}): string | undefined => {
  const { teamId, seksjonId, avdelingId, regionId } = selection
  const { teamOptions, seksjonOptions, avdelingOptions, regionOptions } =
    options

  if (isSelected(teamId)) {
    return teamOptions.find((o) => o.value === teamId)?.label
  }
  if (isSelected(seksjonId)) {
    return seksjonOptions.find((o) => o.value === seksjonId)?.label
  }
  if (isSelected(avdelingId)) {
    return avdelingOptions.find((o) => o.value === avdelingId)?.label
  }
  if (isSelected(regionId)) {
    return regionOptions.find((o) => o.value === regionId)?.label
  }
  return undefined
}

const filterSeksjoner = (
  alleSeksjoner: Seksjon[],
  avdelingId: string,
  regionId: string,
  regionAvdelingIds: Set<string>
): Seksjon[] => {
  if (isSelected(avdelingId)) {
    return alleSeksjoner.filter((s) => s.avdelingId === avdelingId)
  }
  if (isSelected(regionId)) {
    return alleSeksjoner.filter((s) =>
      s.avdelingId
        ? regionAvdelingIds.has(s.avdelingId)
        : s.regionId === regionId
    )
  }
  return alleSeksjoner
}

const filterTeam = (
  alleTeam: Team[],
  seksjonId: string,
  avdelingId: string,
  regionId: string,
  seksjonIds: Set<string>,
  regionAvdelingIds: Set<string>
): Team[] => {
  if (isSelected(seksjonId)) {
    return alleTeam.filter((t) => t.seksjonId === seksjonId)
  }
  if (isSelected(avdelingId)) {
    return alleTeam.filter(
      (t) =>
        t.avdelingId === avdelingId ||
        (t.seksjonId ? seksjonIds.has(t.seksjonId) : false)
    )
  }
  if (isSelected(regionId)) {
    return alleTeam.filter(
      (t) =>
        (t.avdelingId ? regionAvdelingIds.has(t.avdelingId) : false) ||
        (t.seksjonId ? seksjonIds.has(t.seksjonId) : false)
    )
  }
  return alleTeam
}

const buildRegionOptions = (regioner: Region[]): FilterOption[] => [
  { value: ALL_FILTER_VALUE, label: 'Alle' },
  ...regioner
    .slice()
    .sort((a, b) =>
      safeOrgName(a.navn, a.id).localeCompare(
        safeOrgName(b.navn, b.id),
        'nb-NO'
      )
    )
    .map(({ id, navn }) => ({
      value: id,
      label: safeOrgName(navn, id)
    }))
]

const buildAvdelingOptions = (avdelinger: Avdeling[]): FilterOption[] => [
  { value: ALL_FILTER_VALUE, label: 'Alle' },
  ...avdelinger.map(({ id, navn }) => ({
    value: id,
    label: safeOrgName(navn, id)
  }))
]

const buildSeksjonOptions = (seksjoner: Seksjon[]): FilterOption[] => [
  { value: ALL_FILTER_VALUE, label: 'Alle' },
  ...seksjoner.map(({ id, navn }) => ({
    value: id,
    label: safeOrgName(navn, id)
  }))
]

const buildTeamOptions = (teams: Team[]): FilterOption[] => [
  { value: ALL_FILTER_VALUE, label: 'Alle' },
  ...teams.map(({ id, name }) => ({
    value: id,
    label: safeOrgName(name, id)
  }))
]

const buildPlanOptions = (planer: PlanSummary[] | undefined) => [
  { value: ALL_FILTER_VALUE, label: 'Alle' },
  ...(planer
    ?.filter((plan) => plan.parents?.length === 0)
    .sort((a, b) => {
      const aIsActive = a.status === 'active'
      const bIsActive = b.status === 'active'
      if (aIsActive !== bIsActive) {
        return aIsActive ? -1 : 1
      }
      return (a.name ?? a.id.toString()).localeCompare(
        b.name ?? b.id.toString(),
        'nb-NO'
      )
    })
    .map((plan) => ({
      value: plan.id.toString(),
      label:
        plan.status === 'active'
          ? (plan.name ?? plan.id.toString())
          : `${plan.name ?? plan.id.toString()} (Utgått)`,
      status: plan.status
    })) ?? [])
]

const parseSamplePayload = (payload: unknown) => {
  if (payload == null) return undefined
  if (typeof payload === 'object') return payload as Record<string, unknown>
  if (typeof payload !== 'string' || payload.trim().length === 0) {
    return undefined
  }

  try {
    return JSON.parse(payload) as Record<string, unknown>
  } catch {
    return undefined
  }
}

const findEftaNumberInPayload = (
  value: unknown,
  depth = 0
): string | undefined => {
  if (depth > 5 || value == null) return undefined

  const payload = parseSamplePayload(value)
  if (payload == null) return undefined

  const directValue = payload.eftakode
  if (typeof directValue === 'string' || typeof directValue === 'number') {
    return String(directValue)
  }

  for (const nestedValue of Object.values(payload)) {
    const nestedResult = findEftaNumberInPayload(nestedValue, depth + 1)
    if (nestedResult != null) return nestedResult
  }

  return undefined
}

const getSampleEftaNumber = (sample: Sample) => {
  const sampleWithPayload = sample as Sample & { payload?: unknown }
  const payloads = [
    sampleWithPayload.payload,
    ...(sample.subSamples?.map((subSample) => subSample.payload) ?? []),
    ...(sample.requisitions?.map((requisition) => requisition.payload) ?? [])
  ]

  return payloads.map(findEftaNumberInPayload).find(Boolean)
}

const buildEftaNumberOptionsFromValues = (values: string[]): FilterOption[] => [
  { value: ALL_FILTER_VALUE, label: 'Alle' },
  ...sortEftaCodes(values).map((value) => ({ value, label: value }))
]

const getLocationLabel = (value: string, options: FilterOption[]) =>
  options.find((option) => option.value === value)?.label ?? value

const filterSamplesByEftaNumber = (
  samples: Sample[],
  selectedEftaNumber: string
) => {
  if (!isSelected(selectedEftaNumber)) return samples

  return samples.filter(
    (sample) => getSampleEftaNumber(sample) === selectedEftaNumber
  )
}

const buildTypeExternalIdList = (
  statusDuration: Array<{ type: string; externalId: string }> | undefined
) =>
  statusDuration?.map(
    (statusDur) => `${statusDur.type}:${statusDur.externalId}`
  ) ?? []

const buildOwnerNameMap = (
  owners: Array<{ id?: string | number; name?: string | null }> | undefined
) =>
  (owners ?? []).reduce<Record<string, string>>((acc, owner) => {
    acc[String(owner.id)] = String(owner.name ?? '')
    return acc
  }, {})

const buildOrganizationOwnerNames = ({
  regioner,
  avdelinger,
  seksjoner,
  team
}: {
  regioner: Region[]
  avdelinger: Avdeling[]
  seksjoner: Seksjon[]
  team: Team[]
}) => [
  ...regioner.map((region) => ({
    id: `region:${region.id}`,
    name: region.navn,
    type: 'Region'
  })),
  ...avdelinger.map((avdeling) => ({
    id: `avdeling:${avdeling.id}`,
    name: avdeling.navn,
    type: 'Avdeling'
  })),
  ...seksjoner.map((seksjon) => ({
    id: `seksjon:${seksjon.id}`,
    name: seksjon.navn,
    type: 'Seksjon'
  })),
  ...team.map((item) => ({
    id: `team:${item.id}`,
    name: item.name,
    type: 'Team'
  }))
]

const buildPlanNameMap = (
  planSummaries: PlanSummary[] | undefined
): Record<number, string> =>
  (planSummaries ?? []).reduce<Record<number, string>>((acc, planSummary) => {
    acc[planSummary.id] = planSummary.name ?? planSummary.id.toString()
    return acc
  }, {})

const hasPlannedTakenData = (
  rows: Array<{
    samplesPlanned: number
    samplesTaken: number
  }>
) => rows.some((row) => row.samplesPlanned > 0 || row.samplesTaken > 0)

const getFullWidthColumnChartContainerClass = (categoryCount: number) => {
  // Width scales with the number of categories so bars stay a readable width
  // instead of stretching to fill the full page on wide screens.
  if (categoryCount <= 3) return 'mt-2 mx-auto w-full max-w-[44rem]'
  if (categoryCount <= 6) return 'mt-2 mx-auto w-full max-w-[60rem]'
  if (categoryCount <= 10) return 'mt-2 mx-auto w-full max-w-[76rem]'

  return 'mt-2 mx-auto w-full max-w-[96rem]'
}

const getColumnChartClass = () => 'overview-plan-breakdown-chart'

const ChartContainer = ({
  categoryCount,
  fullWidth = true,
  labelHeadroom = false,
  className = '',
  children
}: {
  categoryCount: number
  fullWidth?: boolean
  labelHeadroom?: boolean
  className?: string
  children: React.ReactNode
}) => {
  // Vertical column charts render value labels above the tallest bar (see
  // useChartBarValueLabels), so they opt into extra top padding to keep those
  // labels from overlapping the card header. Other charts don't need it.
  const baseClass = getFullWidthColumnChartContainerClass(categoryCount)
  const withHeadroom = [baseClass, labelHeadroom ? 'pt-12' : '', className]
    .filter(Boolean)
    .join(' ')

  return (
    <Mtds.Grid
      className={
        fullWidth ? withHeadroom : `${withHeadroom} mx-auto max-w-[40rem]`
      }
    >
      {children}
    </Mtds.Grid>
  )
}

const ChartCard = ({
  title,
  children,
  className = '',
  helpText
}: {
  title: React.ReactNode
  children: React.ReactNode
  className?: string
  helpText?: React.ReactNode
}) => (
  <Mtds.Card className={className}>
    <Mtds.Grid>
      <Mtds.Flex data-align='center' data-gap={2} className='flex-wrap'>
        <Mtds.Heading data-size='xs' className='text-lg'>
          {title}
        </Mtds.Heading>
        {helpText != null && (
          <Mtds.HelpText aria-label={`Mer informasjon om ${title}`}>
            {helpText}
          </Mtds.HelpText>
        )}
      </Mtds.Flex>
      {children}
    </Mtds.Grid>
  </Mtds.Card>
)

const ResultMetricCard = ({
  samplesPaavist,
  samplesAnalyzed,
  samplesPaavistPercent
}: {
  samplesPaavist: number
  samplesAnalyzed: number
  samplesPaavistPercent: number
}) => (
  <Mtds.Grid>
    <Mtds.Flex data-align='center' data-gap={2} className='flex-wrap'>
      <Mtds.Heading data-size='sm'>Resultat</Mtds.Heading>
      <Mtds.HelpText aria-label='Mer informasjon om resultat'>
        Funn for valgt år og filter. Andel beregnes fra delprøver med
        konkluderte analyseresultater.
      </Mtds.HelpText>
    </Mtds.Flex>
    <Mtds.Grid data-items={250}>
      <Mtds.Card className='w-full'>
        <Mtds.Info>
          <strong>Analyserte delprøver</strong>
          <Mtds.Heading data-size='2xl'>
            {formatCount(samplesAnalyzed)}
          </Mtds.Heading>
        </Mtds.Info>
      </Mtds.Card>
      <Mtds.Card className='w-full'>
        <Mtds.Info>
          <strong>Påviste delprøver</strong>
          <Mtds.Heading data-size='2xl'>
            {formatCount(samplesPaavist)}
          </Mtds.Heading>
        </Mtds.Info>
      </Mtds.Card>
      <Mtds.Card className='w-full'>
        <Mtds.Info>
          <strong>Andel påvist</strong>
          <Mtds.Heading data-size='2xl'>
            {samplesAnalyzed > 0
              ? formatPercent(samplesPaavistPercent, {
                  maximumFractionDigits: 0
                })
              : 'Ikke beregnet'}
          </Mtds.Heading>
        </Mtds.Info>
      </Mtds.Card>
    </Mtds.Grid>
  </Mtds.Grid>
)

type BreakdownTableRow = {
  key: React.Key
  name: string
  samplesPlanned: number
  samplesTaken: number
  href?: string
}

const BreakdownTable = ({
  rows,
  firstColumnLabel,
  caption,
  targetMetricsApplicable = true
}: {
  rows: BreakdownTableRow[]
  firstColumnLabel: string
  caption: string
  targetMetricsApplicable?: boolean
}) => (
  <figure>
    <figcaption className='sr-only'>{caption}</figcaption>
    <Mtds.Table
      aria-label={caption}
      data-align='center'
      className='w-full table-fixed'
    >
      <colgroup>
        <col className='w-2/5' />
        <col className='w-[15%]' />
        <col className='w-[15%]' />
        <col className='w-[15%]' />
        <col className='w-[15%]' />
      </colgroup>
      <thead>
        <tr>
          <th>{firstColumnLabel}</th>
          <th>Planlagt</th>
          <th>Tatt</th>
          <th>Gjenstår</th>
          <th>Måloppnåelse</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const completionPercent =
            row.samplesPlanned > 0
              ? (row.samplesTaken / row.samplesPlanned) * 100
              : undefined
          const completionLabel =
            completionPercent == null
              ? '-'
              : `${completionPercent.toLocaleString('nb-NO', {
                  maximumFractionDigits: 1
                })} %`

          return (
            <tr key={row.key}>
              <th scope='row'>
                {row.href ? <Link to={row.href}>{row.name}</Link> : row.name}
              </th>
              <td>
                {targetMetricsApplicable
                  ? row.samplesPlanned.toLocaleString('nb-NO')
                  : '–'}
              </td>
              <td>{row.samplesTaken.toLocaleString('nb-NO')}</td>
              <td>
                {targetMetricsApplicable
                  ? Math.max(
                      row.samplesPlanned - row.samplesTaken,
                      0
                    ).toLocaleString('nb-NO')
                  : '–'}
              </td>
              <td>
                <Mtds.Grid data-gap={1} className='min-w-32'>
                  <span>{targetMetricsApplicable ? completionLabel : '–'}</span>
                  {targetMetricsApplicable && completionPercent != null && (
                    <Mtds.Progress
                      value={Math.min(completionPercent, 100)}
                      max={100}
                      aria-label={`Måloppnåelse ${completionLabel}`}
                    />
                  )}
                </Mtds.Grid>
              </td>
            </tr>
          )
        })}
      </tbody>
    </Mtds.Table>
  </figure>
)

const buildOwnerAliasMap = ({
  ownerType,
  teams,
  seksjoner
}: {
  ownerType: DelegationOwnerType
  teams: Team[]
  seksjoner: Seksjon[]
}) => {
  const aliases = new Map<string, string>()
  const seksjonAvdelingIds = new Map(
    seksjoner.map((seksjon) => [seksjon.id, seksjon.avdelingId])
  )

  if (ownerType === 'seksjon') {
    teams.forEach((team) => {
      if (team.seksjonId)
        aliases.set(`team:${team.id}`, `seksjon:${team.seksjonId}`)
    })
  }

  if (ownerType === 'avdeling') {
    seksjoner.forEach((seksjon) => {
      if (seksjon.avdelingId)
        aliases.set(`seksjon:${seksjon.id}`, `avdeling:${seksjon.avdelingId}`)
    })
    teams.forEach((team) => {
      const avdelingId =
        team.avdelingId ?? seksjonAvdelingIds.get(team.seksjonId ?? '')
      if (avdelingId) aliases.set(`team:${team.id}`, `avdeling:${avdelingId}`)
    })
  }

  return aliases
}

const applySampleTakenCountsByPlan = (
  rows: PlanBreakdownRow[],
  sampleCounts: Map<number, number>
): PlanBreakdownRow[] =>
  rows
    .map((row) => ({
      ...row,
      samplesTaken: sampleCounts.get(row.planId) ?? 0
    }))
    .filter((row) => row.samplesPlanned > 0 || row.samplesTaken > 0)
    .sort((a, b) =>
      compareByRemainingThenSize(
        { ...a, name: a.planName },
        { ...b, name: b.planName }
      )
    )

const applySampleTakenCountsByOwner = <
  T extends { unit: string; samplesTaken: number }
>(
  rows: T[],
  sampleCounts: Map<string, number>,
  shouldApply: boolean
): T[] =>
  shouldApply
    ? rows.map((row) => ({
        ...row,
        samplesTaken: sampleCounts.get(row.unit) ?? 0
      }))
    : rows

type StatisticsExportRow = Record<string, unknown>

const blankPlannedTakenRow: StatisticsExportRow = {
  Type: '',
  Kategori: '',
  Planlagt: '',
  Tatt: ''
}

const blankResultRow: StatisticsExportRow = {
  Type: '',
  Kategori: '',
  Påvist: '',
  'Ikke påvist': ''
}

const blankStatusRow: StatisticsExportRow = {
  Type: '',
  Kategori: '',
  Planlagt: '',
  Tatt: '',
  'Måloppnåelse jf planlagt': '',
  'Måloppnåelse jf planlagt (%)': '',
  'Status pr dato': '',
  'Planlagte i år': '',
  'Tatt hittil': '',
  Gjenstår: '',
  Måloppnåelse: '',
  'Planlagt denne måneden': '',
  'Tatt denne måneden': ''
}

const buildExportSectionRows = (
  title: string,
  rows: StatisticsExportRow[],
  blankRow: StatisticsExportRow
) =>
  rows.length > 0
    ? [{ ...blankRow, Type: title }, ...rows, { ...blankRow }]
    : []

const buildStatisticsExportSummaryRows = ({
  monthBreakdownRows,
  ownerBreakdownRows,
  tertialBreakdownRows,
  planBreakdownRows,
  perTypeResult,
  previousMonthStatus,
  samplesToTakePlan,
  samplesTakenPlan,
  targetMetricsApplicable,
  currentMonthData
}: {
  monthBreakdownRows: Array<{
    month: string
    samplesPlanned: number
    samplesTaken: number
  }>
  ownerBreakdownRows: OwnerBreakdownRow[]
  tertialBreakdownRows: Array<{
    tertial: string
    samplesPlanned: number
    samplesTaken: number
  }>
  planBreakdownRows: PlanBreakdownRow[]
  perTypeResult: Array<{
    label: string
    negativeSamples: number
    positiveSamples: number
  }>
  previousMonthStatus: {
    statusDate: string
    planned: number
    taken: number
  }
  samplesToTakePlan: number
  samplesTakenPlan: number
  targetMetricsApplicable: boolean
  currentMonthData: CumulativeChartRow | undefined
}): Array<Record<string, unknown>> => {
  const previousMonthRows =
    previousMonthStatus.planned > 0 || previousMonthStatus.taken > 0
      ? [
          {
            ...blankStatusRow,
            Type: 'Status hittil i år ved utgangen av forrige måned',
            Planlagt: targetMetricsApplicable
              ? previousMonthStatus.planned
              : '',
            Tatt: previousMonthStatus.taken,
            'Måloppnåelse jf planlagt': targetMetricsApplicable
              ? previousMonthStatus.taken - previousMonthStatus.planned
              : '',
            'Måloppnåelse jf planlagt (%)':
              targetMetricsApplicable && previousMonthStatus.planned > 0
                ? formatPercent(
                    (previousMonthStatus.taken / previousMonthStatus.planned) *
                      100
                  )
                : '',
            'Status pr dato': new Date(
              previousMonthStatus.statusDate
            ).toLocaleDateString('nb-NO')
          },
          { ...blankStatusRow }
        ]
      : []

  const totalRows =
    samplesToTakePlan > 0 || samplesTakenPlan > 0
      ? [
          {
            ...blankStatusRow,
            Type: 'Totalt i år',
            'Planlagte i år': targetMetricsApplicable ? samplesToTakePlan : '',
            'Tatt hittil': samplesTakenPlan,
            Gjenstår: targetMetricsApplicable
              ? Math.max(samplesToTakePlan - samplesTakenPlan, 0)
              : '',
            Måloppnåelse: targetMetricsApplicable
              ? samplesToTakePlan > 0
                ? formatPercent((samplesTakenPlan / samplesToTakePlan) * 100)
                : formatPercent(0)
              : ''
          },
          { ...blankStatusRow }
        ]
      : []

  const currentMonthRows = currentMonthData
    ? [
        {
          ...blankStatusRow,
          Type: 'Status denne måneden',
          'Status pr dato': new Date().toLocaleDateString('nb-NO'),
          'Planlagt denne måneden': targetMetricsApplicable
            ? (currentMonthData.samplesPlanned ?? 0)
            : '',
          'Tatt denne måneden': currentMonthData.samplesTaken ?? 0
        }
      ]
    : []

  return [
    ...buildExportSectionRows(
      'Prøveuttak per måned',
      monthBreakdownRows.map((row) => ({
        ...blankPlannedTakenRow,
        Kategori: row.month,
        Planlagt: targetMetricsApplicable ? row.samplesPlanned : '',
        Tatt: row.samplesTaken
      })),
      blankPlannedTakenRow
    ),
    ...buildExportSectionRows(
      'Prøveuttak per seksjon',
      ownerBreakdownRows.map((row) => ({
        ...blankPlannedTakenRow,
        Kategori: row.name,
        Planlagt: targetMetricsApplicable ? row.samplesPlanned : '',
        Tatt: row.samplesTaken
      })),
      blankPlannedTakenRow
    ),
    ...buildExportSectionRows(
      'Prøveuttak per tertial',
      tertialBreakdownRows.map((row) => ({
        ...blankPlannedTakenRow,
        Kategori: row.tertial,
        Planlagt: targetMetricsApplicable ? row.samplesPlanned : '',
        Tatt: row.samplesTaken
      })),
      blankPlannedTakenRow
    ),
    ...buildExportSectionRows(
      'Prøveuttak per plan',
      planBreakdownRows.map((row) => ({
        ...blankPlannedTakenRow,
        Kategori: row.planName,
        Planlagt: targetMetricsApplicable ? row.samplesPlanned : '',
        Tatt: row.samplesTaken
      })),
      blankPlannedTakenRow
    ),
    ...buildExportSectionRows(
      'Påvist per prøvetype',
      perTypeResult.map((row) => ({
        ...blankResultRow,
        Kategori: row.label,
        Påvist: row.positiveSamples ?? 0,
        'Ikke påvist': row.negativeSamples ?? 0
      })),
      blankResultRow
    ),
    ...previousMonthRows,
    ...totalRows,
    ...currentMonthRows
  ]
}

const createJsonForDownload = (dataRemoved: unknown, filename: string) => {
  const dataJson = JSON.stringify(dataRemoved, null, 2)
  const blob = new Blob([dataJson], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const stringifyExportValue = (value: unknown) => {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (value instanceof Date) return value.toLocaleDateString('nb-NO')
  try {
    return JSON.stringify(value) ?? ''
  } catch {
    return ''
  }
}

const escapeCsvValue = (value: unknown) => {
  const stringValue = stringifyExportValue(value)
  return /[";\n\r]/.test(stringValue)
    ? `"${stringValue.replaceAll('"', '""')}"`
    : stringValue
}

const getSectionHeaders = (
  title: string,
  rows: Array<Record<string, unknown>>
) => {
  if (statusSectionTitles.has(title)) {
    return [
      'Type',
      'Planlagt',
      'Tatt',
      'Måloppnåelse jf planlagt',
      'Måloppnåelse jf planlagt (%)',
      'Status pr dato',
      'Planlagte i år',
      'Tatt hittil',
      'Gjenstår',
      'Måloppnåelse',
      'Planlagt denne måneden',
      'Tatt denne måneden'
    ]
  }

  if (title === 'Påvist per prøvetype') {
    return ['Type', 'Kategori', 'Påvist', 'Ikke påvist']
  }

  return getHeadersWithData(rows)
}

const buildCsvSectionRows = (
  section: { title: string; rows: Array<Record<string, unknown>> },
  includeSpacer: boolean
) => {
  const filteredRows = section.rows.filter((row) => hasMeaningfulData(row))
  const headers = getSectionHeaders(section.title, filteredRows)
  const headersWithData =
    filteredRows.length > 0
      ? headers.filter((header) =>
          filteredRows.some((row) => isMeaningfulValue(row[header]))
        )
      : headers

  return [
    section.title,
    headersWithData.map(escapeCsvValue).join(';'),
    ...filteredRows.map((row) =>
      headersWithData.map((header) => escapeCsvValue(row[header])).join(';')
    ),
    ...(includeSpacer ? [''] : [])
  ]
}

const createCsvForDownload = (
  rows: Array<Record<string, unknown>>,
  filename: string
) => {
  if (rows.length === 0) return
  const sections = buildStatisticsSections(rows)
  const csv = sections
    .flatMap((section, index) =>
      buildCsvSectionRows(section, index < sections.length - 1)
    )
    .join('\n')

  downloadBlob(
    new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }),
    filename
  )
}

const statusSectionTitles = new Set([
  'Status hittil i år ved utgangen av forrige måned',
  'Totalt i år',
  'Status denne måneden'
])

const isMeaningfulValue = (value: unknown) =>
  value !== '' && value !== null && value !== undefined

const hasMeaningfulData = (row: Record<string, unknown>) =>
  Object.values(row).some(isMeaningfulValue)

const buildStatisticsSections = (rows: Array<Record<string, unknown>>) => {
  const sections: Array<{
    title: string
    rows: Array<Record<string, unknown>>
  }> = []
  let currentSection: {
    title: string
    rows: Array<Record<string, unknown>>
  } | null = null

  const closeCurrentSection = () => {
    if (currentSection?.rows.length) sections.push(currentSection)
  }

  for (const row of rows) {
    const rowTyped = row as Record<string, unknown>
    const rowData = { ...rowTyped }
    delete rowData.Rapport
    delete rowData.Type
    const reportType = stringifyExportValue(
      rowTyped.Rapport ?? rowTyped.Type
    ).trim()
    const kategori = stringifyExportValue(rowTyped.Kategori).trim()
    const hasRowData = hasMeaningfulData(rowData)

    if (!reportType && !kategori && !hasRowData) continue

    if (reportType) {
      closeCurrentSection()
      currentSection = { title: reportType, rows: [] }

      if (statusSectionTitles.has(reportType) && hasRowData) {
        currentSection.rows.push(rowData)
      }
      continue
    }

    if (currentSection) {
      currentSection.rows.push(rowData)
    }
  }

  closeCurrentSection()

  return sections
}

const getHeadersWithData = (rows: Array<Record<string, unknown>>) => {
  const allHeaders = rows.length > 0 ? Object.keys(rows[0]) : []

  return allHeaders.filter((header) =>
    rows.some((row) => isMeaningfulValue(row[header]))
  )
}

const toPdfTextHex = (value: unknown) => {
  const winAnsiOverrides: Record<string, number> = {
    '€': 0x80,
    '‘': 0x91,
    '’': 0x92,
    '“': 0x93,
    '”': 0x94,
    '–': 0x96,
    '—': 0x97,
    '…': 0x85
  }

  return Array.from(stringifyExportValue(value))
    .map((char) => {
      const code = char.codePointAt(0) ?? 0
      const winAnsiCode = code <= 0xff ? code : (winAnsiOverrides[char] ?? 0x3f)
      return winAnsiCode.toString(16).padStart(2, '0')
    })
    .join('')
    .toUpperCase()
}

const truncatePdfText = (value: unknown, maxLength: number) => {
  const text = stringifyExportValue(value)
  if (text.length <= maxLength) return text
  return `${text.slice(0, Math.max(maxLength - 1, 0))}…`
}

const getStringByteLength = (value: string) => new Blob([value]).size

const createPdfBlob = (pageStreams: string[]) => {
  const objects: string[] = []
  const pageObjectIds = pageStreams.map((_, index) => 4 + index * 2)
  const contentObjectIds = pageStreams.map((_, index) => 5 + index * 2)

  objects[0] = '<< /Type /Catalog /Pages 2 0 R >>'
  objects[1] = `<< /Type /Pages /Kids [${pageObjectIds
    .map((id) => `${id} 0 R`)
    .join(' ')}] /Count ${pageStreams.length} >>`
  objects[2] =
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>'

  pageStreams.forEach((stream, index) => {
    const pageObjectId = pageObjectIds[index]
    const contentObjectId = contentObjectIds[index]
    objects[pageObjectId - 1] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 842 595] /Resources << /Font << /F1 3 0 R >> >> /Contents ${contentObjectId} 0 R >>`
    objects[contentObjectId - 1] =
      `<< /Length ${getStringByteLength(stream)} >>\nstream\n${stream}\nendstream`
  })

  let pdf = '%PDF-1.4\n%\xE2\xE3\xCF\xD3\n'
  const offsets = [0]

  objects.forEach((object, index) => {
    offsets.push(getStringByteLength(pdf))
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`
  })

  const xrefOffset = getStringByteLength(pdf)
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`
  offsets.slice(1).forEach((offset) => {
    pdf += `${offset.toString().padStart(10, '0')} 00000 n \n`
  })
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  return new Blob([pdf], { type: 'application/pdf' })
}

const createStatisticsPdfForDownload = ({
  title,
  filters,
  rows,
  filename
}: {
  title: string
  filters: string[]
  rows: Array<Record<string, unknown>>
  filename: string
}) => {
  const generatedAt = new Date().toLocaleString('nb-NO')
  const sections = buildStatisticsSections(rows)
  const pageWidth = 842
  const pageHeight = 595
  const margin = 34
  const contentWidth = pageWidth - margin * 2
  const minY = 34
  const pageStreams: string[] = []
  let commands: string[] = []
  let y = pageHeight - margin

  const addPage = () => {
    if (commands.length > 0) pageStreams.push(commands.join('\n'))
    commands = ['0.5 w']
    y = pageHeight - margin
  }

  const ensureSpace = (height: number) => {
    if (y - height < minY) addPage()
  }

  const drawText = (
    text: unknown,
    x: number,
    textY: number,
    fontSize: number,
    color = '0 0 0'
  ) => {
    commands.push(
      `${color} rg BT /F1 ${fontSize} Tf ${x.toFixed(2)} ${textY.toFixed(
        2
      )} Td <${toPdfTextHex(text)}> Tj ET`
    )
  }

  const drawRect = (
    x: number,
    rectY: number,
    width: number,
    height: number,
    fillColor?: string
  ) => {
    if (fillColor) {
      commands.push(
        `${fillColor} rg ${x.toFixed(2)} ${rectY.toFixed(2)} ${width.toFixed(
          2
        )} ${height.toFixed(2)} re f`
      )
    }
    commands.push(
      `0.82 0.82 0.82 RG ${x.toFixed(2)} ${rectY.toFixed(
        2
      )} ${width.toFixed(2)} ${height.toFixed(2)} re S`
    )
  }

  addPage()
  drawText(title, margin, y, 18)
  y -= 22
  drawText(`Generert: ${generatedAt}`, margin, y, 9, '0.35 0.35 0.35')
  y -= 16
  drawText(
    `Filter: ${filters.length > 0 ? filters.join(' | ') : 'Ingen'}`,
    margin,
    y,
    9,
    '0.25 0.25 0.25'
  )
  y -= 24

  const generateTableForGroup = (
    reportType: string,
    groupRows: Array<Record<string, unknown>>
  ) => {
    const filteredRows = groupRows.filter((row) => hasMeaningfulData(row))

    if (filteredRows.length === 0) return

    const usedHeaders = getHeadersWithData(filteredRows)
    const rowHeight = 16
    const columnWidth = contentWidth / Math.max(usedHeaders.length, 1)
    const maxChars = Math.max(Math.floor(columnWidth / 3.7), 4)

    ensureSpace(40)
    drawText(reportType, margin, y, 13, '0.1 0.1 0.1')
    y -= 16

    drawRect(
      margin,
      y - rowHeight + 3,
      contentWidth,
      rowHeight,
      '0.29 0.56 0.85'
    )
    usedHeaders.forEach((header, index) => {
      drawText(
        truncatePdfText(header, maxChars),
        margin + index * columnWidth + 3,
        y - 8,
        6,
        '1 1 1'
      )
    })
    y -= rowHeight

    filteredRows.forEach((row, rowIndex) => {
      ensureSpace(rowHeight + 4)
      drawRect(
        margin,
        y - rowHeight + 3,
        contentWidth,
        rowHeight,
        rowIndex % 2 === 0 ? '0.97 0.97 0.97' : undefined
      )
      usedHeaders.forEach((header, index) => {
        drawText(
          truncatePdfText(row[header], maxChars),
          margin + index * columnWidth + 3,
          y - 8,
          6,
          '0 0 0'
        )
      })
      y -= rowHeight
    })

    y -= 14
  }

  sections.forEach((section) =>
    generateTableForGroup(section.title, section.rows)
  )
  if (commands.length > 0) pageStreams.push(commands.join('\n'))

  downloadBlob(createPdfBlob(pageStreams), filename)
}

const useReportingOrgData = ({
  organizationUnits,
  selectedRegionId,
  selectedAvdelingId,
  selectedSeksjonId
}: {
  organizationUnits: Array<{
    id: string
    name: string
    shortName?: string
    type: 'region' | 'avdeling' | 'seksjon' | 'team'
    parentId?: string
  }>
  selectedRegionId: string
  selectedAvdelingId: string
  selectedSeksjonId: string
}) => {
  const regioner = React.useMemo<Region[]>(
    () =>
      organizationUnits
        .filter((unit) => unit.type === 'region')
        .map((unit) => ({
          id: unit.id,
          navn: unit.name,
          kortnavn: unit.shortName ?? unit.name
        })),
    [organizationUnits]
  )
  const avdelinger = React.useMemo<Avdeling[]>(
    () =>
      organizationUnits
        .filter((unit) => unit.type === 'avdeling')
        .map((unit) => ({
          id: unit.id,
          navn: unit.name,
          kortnavn: unit.shortName ?? unit.name,
          regionId: unit.parentId
        })),
    [organizationUnits]
  )

  const selectedRegionAvdelinger = React.useMemo(
    () =>
      selectedRegionId === ALL_FILTER_VALUE
        ? avdelinger
        : avdelinger.filter(
            (avdeling) => avdeling.regionId === selectedRegionId
          ),
    [avdelinger, selectedRegionId]
  )

  const alleSeksjoner = React.useMemo<Seksjon[]>(
    () =>
      organizationUnits
        .filter((unit) => unit.type === 'seksjon')
        .map((unit) => ({
          id: unit.id,
          navn: unit.name,
          kortnavn: unit.shortName ?? unit.name,
          avdelingId: unit.parentId
        })),
    [organizationUnits]
  )

  const selectedRegionAvdelingIds = React.useMemo(
    () => new Set(selectedRegionAvdelinger.map((avdeling) => avdeling.id)),
    [selectedRegionAvdelinger]
  )

  const selectedAvdelingSeksjoner = React.useMemo(
    () =>
      filterSeksjoner(
        alleSeksjoner,
        selectedAvdelingId,
        selectedRegionId,
        selectedRegionAvdelingIds
      ),
    [
      alleSeksjoner,
      selectedAvdelingId,
      selectedRegionAvdelingIds,
      selectedRegionId
    ]
  )

  const alleTeam = React.useMemo<Team[]>(() => {
    const seksjonIds = new Set(alleSeksjoner.map((seksjon) => seksjon.id))
    return organizationUnits
      .filter((unit) => unit.type === 'team')
      .map((unit) => ({
        id: unit.id,
        name: unit.name,
        seksjonId:
          unit.parentId && seksjonIds.has(unit.parentId)
            ? unit.parentId
            : undefined,
        avdelingId:
          unit.parentId && !seksjonIds.has(unit.parentId)
            ? unit.parentId
            : undefined
      }))
  }, [alleSeksjoner, organizationUnits])

  const selectedSeksjonIds = React.useMemo(
    () => new Set(selectedAvdelingSeksjoner.map((seksjon) => seksjon.id)),
    [selectedAvdelingSeksjoner]
  )

  const selectedTeam = React.useMemo(
    () =>
      filterTeam(
        alleTeam,
        selectedSeksjonId,
        selectedAvdelingId,
        selectedRegionId,
        selectedSeksjonIds,
        selectedRegionAvdelingIds
      ),
    [
      alleTeam,
      selectedAvdelingId,
      selectedRegionAvdelingIds,
      selectedRegionId,
      selectedSeksjonId,
      selectedSeksjonIds
    ]
  )

  return {
    regioner,
    avdelinger,
    alleSeksjoner,
    alleTeam,
    selectedRegionAvdelinger,
    selectedAvdelingSeksjoner,
    selectedTeam
  }
}

// --- End helper functions ---

export const StatisticsScreen = () => {
  const [selectedPlan, setSelectedPlan] = React.useState<number | undefined>(
    undefined
  )
  const [selectedRegionId, setSelectedRegionId] =
    React.useState<string>(ALL_FILTER_VALUE)
  const [selectedAvdelingId, setSelectedAvdelingId] =
    React.useState<string>(ALL_FILTER_VALUE)
  const [selectedSeksjonId, setSelectedSeksjonId] =
    React.useState<string>(ALL_FILTER_VALUE)
  const [selectedTeamId, setSelectedTeamId] =
    React.useState<string>(ALL_FILTER_VALUE)
  const [selectedEftaCode, setSelectedEftaCode] =
    React.useState<string>(ALL_FILTER_VALUE)
  const [isSampleExportRequested, setIsSampleExportRequested] =
    React.useState(false)
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = React.useState(currentYear)
  const monthBreakdownChartRef = React.useRef<ChartElementRef | null>(null)
  const tertialBreakdownChartRef = React.useRef<ChartElementRef | null>(null)
  const perTypeResultChartRef = React.useRef<ChartElementRef | null>(null)
  const sampleTypeDistributionChartRef = React.useRef<ChartElementRef | null>(
    null
  )
  const sampleDurationChartRef = React.useRef<ChartElementRef | null>(null)

  const { authFetch } = useAuth()
  const { data: planer, isPending: isPendingPlans } = useGetPlanSummariesQuery()
  const organizationUnitsQuery = useGetOrganizationUnitsQuery()

  const {
    regioner,
    avdelinger,
    alleSeksjoner,
    alleTeam,
    selectedRegionAvdelinger,
    selectedAvdelingSeksjoner,
    selectedTeam
  } = useReportingOrgData({
    organizationUnits: organizationUnitsQuery.data ?? [],
    selectedRegionId,
    selectedAvdelingId,
    selectedSeksjonId
  })
  const isLoadingOrganizationUnits = organizationUnitsQuery.isLoading

  const regionOptions = React.useMemo(
    () => buildRegionOptions(regioner),
    [regioner]
  )

  const planOptions = React.useMemo(() => buildPlanOptions(planer), [planer])

  const planNameMap = React.useMemo(() => buildPlanNameMap(planer), [planer])

  const organizationOwnerNames = React.useMemo(
    () =>
      buildOrganizationOwnerNames({
        regioner,
        avdelinger,
        seksjoner: alleSeksjoner,
        team: alleTeam
      }),
    [alleSeksjoner, alleTeam, avdelinger, regioner]
  )
  const organizationOwnerNameMap = React.useMemo(
    () => buildOwnerNameMap(organizationOwnerNames),
    [organizationOwnerNames]
  )

  const avdelingOptions = React.useMemo(
    () => buildAvdelingOptions(selectedRegionAvdelinger),
    [selectedRegionAvdelinger]
  )

  const seksjonOptions = React.useMemo(
    () => buildSeksjonOptions(selectedAvdelingSeksjoner),
    [selectedAvdelingSeksjoner]
  )

  const teamOptions = React.useMemo(
    () => buildTeamOptions(selectedTeam),
    [selectedTeam]
  )

  const generateExportFilename = (prefix: string, extension: string) => {
    const now = new Date()
    const dateStr = now
      .toLocaleDateString('nb-NO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      .replaceAll('.', '-')
    const timeStr = now
      .toLocaleTimeString('nb-NO', {
        hour: '2-digit',
        minute: '2-digit'
      })
      .replaceAll(':', '-')
    return `${prefix}_${dateStr}_${timeStr}.${extension}`
  }

  const selectedOwner = React.useMemo(
    () =>
      computeSelectedOwner(
        selectedTeamId,
        selectedSeksjonId,
        selectedAvdelingId,
        selectedRegionId
      ),
    [selectedAvdelingId, selectedRegionId, selectedSeksjonId, selectedTeamId]
  )

  const resetOrgFilters = React.useCallback(() => {
    setSelectedPlan(undefined)
    setSelectedYear(currentYear)
    setSelectedRegionId(ALL_FILTER_VALUE)
    setSelectedAvdelingId(ALL_FILTER_VALUE)
    setSelectedSeksjonId(ALL_FILTER_VALUE)
    setSelectedTeamId(ALL_FILTER_VALUE)
    setSelectedEftaCode(ALL_FILTER_VALUE)
  }, [currentYear])

  const selectedYearDateRange = React.useMemo(
    () => getOsloYearDateRange(selectedYear),
    [selectedYear]
  )
  const ownerBreakdownLevel = isSelected(selectedTeamId)
    ? undefined
    : isSelected(selectedSeksjonId)
      ? 'team'
      : isSelected(selectedAvdelingId)
        ? 'seksjon'
        : 'avdeling'

  const sampleTakingReportQuery = useGetSampleTakingReportQuery({
    from: `${selectedYear}-01-01`,
    through: `${selectedYear}-12-31`,
    planId: selectedPlan,
    ownerType: selectedOwner?.type === 'user' ? undefined : selectedOwner?.type,
    ownerId: selectedOwner?.type === 'user' ? undefined : selectedOwner?.owner,
    ownerScope: 'subtree',
    eftaCode: isSelected(selectedEftaCode) ? selectedEftaCode : undefined,
    ownerBreakdownLevel: ownerBreakdownLevel
  })

  const {
    samples,
    isComplete: areAllSamplesLoaded,
    isError,
    progress
  } = useAllSamples({
    isEnabled: isSampleExportRequested,
    ownerIds: selectedOwner ? [selectedOwner] : undefined,
    ownerScope: selectedOwner ? 'subtree' : undefined,
    filters: {
      planIds: selectedPlan ? [String(selectedPlan)] : [],
      productIds: [],
      matrixIds: [],
      sampntTypeIds: [],
      substanceIds: [],
      status: [],
      fromDate: selectedYearDateRange.fromDate,
      toDate: selectedYearDateRange.toDate
    }
  })

  const allYearSamples = React.useMemo(
    () => getSamplesForYear(samples, selectedYear),
    [samples, selectedYear]
  )

  const rawEftaCodeOptions = React.useMemo(
    () =>
      buildEftaNumberOptionsFromValues(
        sampleTakingReportQuery.data?.facets.eftaCodes.map(
          (facet) => facet.code
        ) ?? []
      ),
    [sampleTakingReportQuery.data?.facets.eftaCodes]
  )
  const eftaCodeOptions = useEftaNumberOptions(
    rawEftaCodeOptions,
    sampleTakingReportQuery.isSuccess
  )
  const effectiveEftaCode = eftaCodeOptions.some(
    (option) => option.value === selectedEftaCode
  )
    ? selectedEftaCode
    : ALL_FILTER_VALUE

  React.useEffect(() => {
    if (
      selectedEftaCode !== ALL_FILTER_VALUE &&
      sampleTakingReportQuery.data != null &&
      effectiveEftaCode === ALL_FILTER_VALUE
    ) {
      queueMicrotask(() => setSelectedEftaCode(ALL_FILTER_VALUE))
    }
  }, [selectedEftaCode, sampleTakingReportQuery.data, effectiveEftaCode])

  const { data: statusDuration } = useStatusDurationReport(
    selectedPlan === undefined
  )
  const isLoading = sampleTakingReportQuery.isLoading || isPendingPlans

  const selectedPlanSummary = React.useMemo(
    () => planer?.find((planSummary) => planSummary.id === selectedPlan),
    [planer, selectedPlan]
  )

  const exportFilterParts = React.useMemo(() => {
    const selectedOrgFilterLabel = findSelectedOrgFilterLabel({
      selection: {
        teamId: selectedTeamId,
        seksjonId: selectedSeksjonId,
        avdelingId: selectedAvdelingId,
        regionId: selectedRegionId
      },
      options: {
        teamOptions,
        seksjonOptions,
        avdelingOptions,
        regionOptions
      }
    })

    return [
      selectedPlanSummary?.name,
      selectedYear.toString(),
      selectedOrgFilterLabel,
      isSelected(effectiveEftaCode)
        ? `EFTA ${getLocationLabel(effectiveEftaCode, eftaCodeOptions)}`
        : undefined
    ]
  }, [
    avdelingOptions,
    eftaCodeOptions,
    regionOptions,
    seksjonOptions,
    selectedAvdelingId,
    selectedPlanSummary?.name,
    selectedRegionId,
    selectedSeksjonId,
    selectedTeamId,
    selectedYear,
    teamOptions,
    effectiveEftaCode
  ])

  // Build filter description for export - only include filters that have actual values (not empty)
  const exportFilterPartsForFilename = React.useMemo(() => {
    const orgFilterParts = [
      { prefix: 'Region', value: selectedRegionId, options: regionOptions },
      {
        prefix: 'Avdeling',
        value: selectedAvdelingId,
        options: avdelingOptions
      },
      { prefix: 'Seksjon', value: selectedSeksjonId, options: seksjonOptions },
      { prefix: 'Team', value: selectedTeamId, options: teamOptions }
    ].flatMap(({ prefix, value, options }) => {
      if (!isSelected(value)) return []

      const label = options.find((option) => option.value === value)?.label
      return label ? [`${prefix}_${label}`] : []
    })

    const planName = selectedPlan
      ? planer?.find((p) => p.id === selectedPlan)?.name
      : undefined

    return [
      `År_${selectedYear}`,
      ...orgFilterParts,
      planName ? `Plan_${planName}` : undefined,
      isSelected(effectiveEftaCode) ? `EFTA_${effectiveEftaCode}` : undefined
    ]
      .filter((part): part is string => Boolean(part))
      .map(getFilenameSafePart)
      .filter((part) => part.length > 0)
  }, [
    selectedYear,
    selectedRegionId,
    selectedAvdelingId,
    selectedSeksjonId,
    selectedTeamId,
    selectedPlan,
    planer,
    effectiveEftaCode,
    regionOptions,
    avdelingOptions,
    seksjonOptions,
    teamOptions
  ])

  const selectedPlanDue = React.useMemo(
    () => planer?.find((planSummary) => planSummary.id === selectedPlan)?.due,
    [planer, selectedPlan]
  )
  const selectedPlanTargetYear = getYearFromDate(selectedPlanDue)

  const yearOptions = React.useMemo(
    () =>
      getStatisticsYears({
        currentYear,
        plans: planer,
        selectedPlanDue
      }).map((year) => ({
        value: year.toString(),
        label: year.toString()
      })),
    [currentYear, planer, selectedPlan, selectedPlanDue, selectedPlanTargetYear]
  )

  const onSelectedPlanChange = React.useCallback(
    (planId: number | undefined) => {
      setSelectedPlan(planId)
      if (planId == null) return

      const targetYear = getYearFromDate(
        planer?.find((candidate) => candidate.id === planId)?.due
      )
      if (targetYear != null) setSelectedYear(targetYear)
    },
    [planer]
  )

  const selectedPlanOwnerIds = React.useMemo(
    () =>
      computeSelectedPlanOwnerIds(
        selectedTeamId,
        selectedSeksjonId,
        selectedAvdelingId,
        selectedRegionId,
        selectedRegionAvdelinger
      ),
    [
      selectedAvdelingId,
      selectedRegionAvdelinger,
      selectedRegionId,
      selectedSeksjonId,
      selectedTeamId
    ]
  )

  const selectedYearSamples = React.useMemo(
    () => filterSamplesByEftaNumber(allYearSamples, effectiveEftaCode),
    [allYearSamples, effectiveEftaCode]
  )

  const statisticsError = sampleTakingReportQuery.isError

  const planBreakdownRowsWithoutCounts = React.useMemo(
    () =>
      (sampleTakingReportQuery.data?.breakdowns.plans ?? []).map((row, id) => ({
        id,
        planId: row.plan.id,
        planName:
          row.plan.name || planNameMap[row.plan.id] || `Plan ${row.plan.id}`,
        samplesPlanned: Number(row.measures.planExecution?.plannedUnitCount),
        samplesTaken: 0
      })),
    [planNameMap, sampleTakingReportQuery.data?.breakdowns.plans]
  )

  const reportPlanExecution = React.useMemo(() => {
    const counts = new Map(
      (sampleTakingReportQuery.data?.breakdowns.plans ?? []).map((row) => [
        row.plan.id,
        Number(row.measures.planExecution?.performedUnitCount)
      ])
    )
    return { counts }
  }, [sampleTakingReportQuery.data])

  const samplesTakenPlan =
    Number(
      sampleTakingReportQuery.data?.summary.planExecution?.performedUnitCount
    ) || 0
  const samplesToTakePlan =
    Number(
      sampleTakingReportQuery.data?.summary.planExecution?.plannedUnitCount
    ) || 0
  const {
    analyzedSubsampleCount: samplesAnalyzed,
    positiveSubsampleCount: samplesPaavist,
    positivePercent: samplesPaavistPercent
  } = getSubsampleResultMetrics(
    Number(
      sampleTakingReportQuery.data?.summary.activity.analyzedSubsampleCount
    ) || 0,
    Number(
      sampleTakingReportQuery.data?.summary.activity.positiveSubsampleCount
    ) || 0
  )

  const perTypeResult = React.useMemo(
    () =>
      (sampleTakingReportQuery.data?.breakdowns.products ?? [])
        .map((row) => ({
          id: row.product.id,
          label: row.product.name,
          positiveSamples: Number(row.positiveSampleCount),
          negativeSamples: Number(row.negativeSampleCount),
          totalSamples: Number(row.physicalSampleCount)
        }))
        .sort(
          (a, b) =>
            b.positiveSamples +
              b.negativeSamples -
              (a.positiveSamples + a.negativeSamples) ||
            a.label.localeCompare(b.label)
        ),
    [sampleTakingReportQuery.data?.breakdowns.products]
  )
  const hasSamplesTaken =
    (Number(
      sampleTakingReportQuery.data?.summary.activity.physicalSampleCount
    ) || 0) > 0
  const sampleTypeDistributionData = React.useMemo(
    () => [
      ['Produkt', 'Påviste prøver'],
      ...perTypeResult
        .filter((row) => row.positiveSamples > 0)
        .map<[string, number]>((row) => [row.label, row.positiveSamples])
        .sort((a, b) => b[1] - a[1])
    ],
    [perTypeResult]
  )

  const yearlyPlanVsTaken = React.useMemo(
    () =>
      (sampleTakingReportQuery.data?.timeSeries ?? []).map((row, id) => ({
        id,
        month: new Date(`${row.period.from}T12:00:00`)
          .toLocaleString('nb-NO', { month: 'short' })
          .replace(/^./, (character) => character.toUpperCase()),
        samplesTaken: Number(row.measures.planExecution?.performedUnitCount),
        samplesPlanned: Number(row.measures.planExecution?.plannedUnitCount)
      })),
    [sampleTakingReportQuery.data?.timeSeries]
  )

  const cumulativeYearlyPlanVsTaken = React.useMemo(
    () =>
      buildCumulativeCalendarSeries(yearlyPlanVsTaken, { year: selectedYear }),
    [selectedYear, yearlyPlanVsTaken]
  )

  const hasCumulativeYearlyData = React.useMemo(
    () => hasCumulativeData(cumulativeYearlyPlanVsTaken),
    [cumulativeYearlyPlanVsTaken]
  )

  const currentMonthIndex = getCurrentMonthIndexForYear(selectedYear)
  const statusMonthIndex = getStatusMonthIndex(selectedYear)
  const isCurrentReportingYear = selectedYear === new Date().getFullYear()

  const previousMonthStatus = React.useMemo(
    () =>
      buildStatusAtEndOfPreviousMonth(
        cumulativeYearlyPlanVsTaken,
        selectedYear,
        statusMonthIndex
      ),
    [cumulativeYearlyPlanVsTaken, selectedYear, statusMonthIndex]
  )

  const currentMonthData = cumulativeYearlyPlanVsTaken[currentMonthIndex]
  const completionPercent =
    samplesToTakePlan > 0 ? (samplesTakenPlan / samplesToTakePlan) * 100 : 0
  const hasEftaCodeFilter = isSelected(effectiveEftaCode)
  const targetMetricsApplicable =
    !hasEftaCodeFilter &&
    (selectedPlan == null || selectedPlanTargetYear === selectedYear)
  const targetMetricsUnavailableHelpText = hasEftaCodeFilter
    ? 'Planlagte prøveuttak er ikke fordelt på EFTA-nummer.'
    : undefined
  const hasRelevantPlannedTakenData = React.useCallback(
    (rows: Array<{ samplesPlanned: number; samplesTaken: number }>) =>
      targetMetricsApplicable
        ? hasPlannedTakenData(rows)
        : rows.some((row) => row.samplesTaken > 0),
    [targetMetricsApplicable]
  )

  const cumulativeChartRows = cumulativeYearlyPlanVsTaken
  const typeExternalIdList = buildTypeExternalIdList(statusDuration)
  const unresolvedDurationOwnerIds = typeExternalIdList.filter(
    (ownerId) => organizationOwnerNameMap[ownerId] == null
  )
  const ownerIdNamesDuration = useGetOwnerNameQueries(
    selectedPlan === undefined && !isLoadingOrganizationUnits
      ? unresolvedDurationOwnerIds
      : []
  )
  const sampleDurationStat = React.useMemo(() => {
    if (!statusDuration) return null
    const safeOwners = [
      ...organizationOwnerNames,
      ...(ownerIdNamesDuration.data ?? []).map((o) => ({
        ...o,
        name: o.name ?? '',
        type: o.type ?? ''
      }))
    ]
    return mapArrayToSamplesDurationStatusAvdeling(statusDuration, safeOwners)
  }, [organizationOwnerNames, ownerIdNamesDuration.data, statusDuration])

  const sortedSampleDurationStat = React.useMemo(() => {
    if (sampleDurationStat == null) return null
    const visibleStats = filterExpiredOrganizationUnits(sampleDurationStat)
    return [...visibleStats].sort((a, b) => {
      const diff = getSampleDurationLeadTime(b) - getSampleDurationLeadTime(a)
      if (diff !== 0) return diff
      return a.name.localeCompare(b.name, 'nb-NO')
    })
  }, [sampleDurationStat])

  const sampleDurationSummary = React.useMemo(() => {
    if (
      sortedSampleDurationStat == null ||
      sortedSampleDurationStat.length === 0
    ) {
      return null
    }

    const rows = sortedSampleDurationStat
    const count = rows.length
    const leadTimes = rows.map(getSampleDurationLeadTime)
    const averageLeadTime =
      leadTimes.reduce((sum, value) => sum + value, 0) / count

    const averageOf = (selector: (row: (typeof rows)[number]) => number) =>
      rows.reduce((sum, row) => sum + selector(row), 0) / count

    const averagesPerStatus = [
      { label: 'Registrert', average: averageOf((row) => row.sampleTaken) },
      { label: 'Sendt', average: averageOf((row) => row.sampleSent) },
      { label: 'Mottatt lab', average: averageOf((row) => row.sampleReceived) },
      {
        label: 'Analyse startet',
        average: averageOf((row) => row.analysisStarted)
      },
      {
        label: 'Klar til behandling',
        average: averageOf((row) => row.readyToProcess)
      },
      { label: 'Kansellert', average: averageOf((row) => row.compromised) }
    ]

    return {
      averageLeadTime,
      averagesPerStatus
    }
  }, [sortedSampleDurationStat])

  const monthBreakdownRows = React.useMemo(
    () => yearlyPlanVsTaken,
    [yearlyPlanVsTaken]
  )

  const tertialBreakdownRows = React.useMemo(
    () =>
      ['T1', 'T2', 'T3'].map((tertial, id) => {
        const months = monthBreakdownRows.slice(id * 4, id * 4 + 4)
        return {
          id,
          tertial,
          samplesPlanned: months.reduce(
            (sum, row) => sum + row.samplesPlanned,
            0
          ),
          samplesTaken: months.reduce((sum, row) => sum + row.samplesTaken, 0)
        }
      }),
    [monthBreakdownRows]
  )

  const seksjonBreakdownOwnerIds = React.useMemo(
    () =>
      selectedAvdelingSeksjoner.length > 0
        ? new Set(
            selectedAvdelingSeksjoner.map((seksjon) => `seksjon:${seksjon.id}`)
          )
        : undefined,
    [selectedAvdelingSeksjoner]
  )

  const ownerBreakdownConfig = React.useMemo(() => {
    if (isSelected(selectedTeamId)) return undefined

    if (isSelected(selectedSeksjonId)) {
      return {
        ownerType: 'team' as const,
        ownerIds: new Set(selectedTeam.map((team) => `team:${team.id}`))
      }
    }

    if (isSelected(selectedAvdelingId)) {
      return {
        ownerType: 'seksjon' as const,
        ownerIds: seksjonBreakdownOwnerIds
      }
    }

    return {
      ownerType: 'avdeling' as const,
      ownerIds: selectedPlanOwnerIds
    }
  }, [
    seksjonBreakdownOwnerIds,
    selectedAvdelingId,
    selectedPlanOwnerIds,
    selectedSeksjonId,
    selectedTeam,
    selectedTeamId
  ])

  const ownerBreakdownLabel = ownerBreakdownConfig
    ? getOwnerTypeLabel(ownerBreakdownConfig.ownerType)
    : undefined

  const ownerBreakdownRowsWithoutNames = React.useMemo(
    () =>
      ownerBreakdownConfig
        ? (sampleTakingReportQuery.data?.breakdowns.owners ?? []).map(
            (row, id) => ({
              id,
              unit: `${row.owner.type}:${row.owner.id}`,
              name: row.owner.name ?? `${row.owner.type}:${row.owner.id}`,
              samplesPlanned: Number(
                row.measures.planExecution?.plannedUnitCount
              ),
              samplesTaken: 0
            })
          )
        : [],
    [ownerBreakdownConfig, sampleTakingReportQuery.data?.breakdowns.owners]
  )

  const ownerBreakdownSampleCounts = React.useMemo(() => {
    if (!ownerBreakdownConfig) return new Map<string, number>()
    const aliases = buildOwnerAliasMap({
      ownerType: ownerBreakdownConfig.ownerType,
      teams: alleTeam,
      seksjoner: selectedAvdelingSeksjoner
    })
    const counts = new Map<string, number>()
    for (const row of sampleTakingReportQuery.data?.breakdowns.owners ?? []) {
      const reportOwnerId = `${row.owner.type}:${row.owner.id}`
      const ownerId = aliases.get(reportOwnerId) ?? reportOwnerId
      if (
        ownerBreakdownConfig.ownerIds &&
        !ownerBreakdownConfig.ownerIds.has(ownerId)
      ) {
        continue
      }
      counts.set(
        ownerId,
        (counts.get(ownerId) ?? 0) +
          Number(row.measures.planExecution?.performedUnitCount)
      )
    }
    return counts
  }, [
    alleTeam,
    ownerBreakdownConfig,
    selectedAvdelingSeksjoner,
    sampleTakingReportQuery.data?.breakdowns.owners
  ])

  const ownerBreakdownNames = useGetOwnerNameQueries(
    isLoadingOrganizationUnits
      ? []
      : ownerBreakdownRowsWithoutNames
          .map((row) => row.unit)
          .filter((ownerId) => organizationOwnerNameMap[ownerId] == null)
  )
  const ownerBreakdownNameMap = React.useMemo(
    () => ({
      ...organizationOwnerNameMap,
      ...buildOwnerNameMap(ownerBreakdownNames.data)
    }),
    [organizationOwnerNameMap, ownerBreakdownNames.data]
  )

  const ownerBreakdownRows: OwnerBreakdownRow[] = React.useMemo(
    () =>
      applySampleTakenCountsByOwner(
        ownerBreakdownRowsWithoutNames,
        ownerBreakdownSampleCounts,
        true
      ).map((row) => ({
        ...row,
        name: ownerBreakdownNameMap[row.unit] || row.name
      })),
    [
      ownerBreakdownNameMap,
      ownerBreakdownRowsWithoutNames,
      ownerBreakdownSampleCounts
    ]
  )

  const planBreakdownRows = React.useMemo(
    () =>
      applySampleTakenCountsByPlan(
        planBreakdownRowsWithoutCounts,
        reportPlanExecution.counts
      ),
    [reportPlanExecution.counts, planBreakdownRowsWithoutCounts]
  )
  const visibleOwnerBreakdownRows = React.useMemo(
    () =>
      targetMetricsApplicable
        ? ownerBreakdownRows
        : ownerBreakdownRows.filter((row) => row.samplesTaken > 0),
    [ownerBreakdownRows, targetMetricsApplicable]
  )
  const visiblePlanBreakdownRows = React.useMemo(
    () =>
      targetMetricsApplicable
        ? planBreakdownRows
        : planBreakdownRows.filter((row) => row.samplesTaken > 0),
    [planBreakdownRows, targetMetricsApplicable]
  )

  const paddedMonthBreakdownRows = React.useMemo(
    () =>
      padChartColumns({
        labels: monthBreakdownRows.map((row) => row.month),
        rows: [
          ...(targetMetricsApplicable
            ? [
                {
                  label: 'Planlagt',
                  values: monthBreakdownRows.map((row) => row.samplesPlanned)
                }
              ]
            : []),
          {
            label: 'Tatt',
            values: monthBreakdownRows.map((row) => row.samplesTaken)
          }
        ]
      }),
    [monthBreakdownRows, targetMetricsApplicable]
  )

  const paddedMonthBreakdownData = React.useMemo(
    () => buildPaddedChartData(paddedMonthBreakdownRows, 'Måned'),
    [paddedMonthBreakdownRows]
  )

  const paddedTertialBreakdownRows = React.useMemo(
    () =>
      padChartColumns({
        labels: tertialBreakdownRows.map((row) => row.tertial),
        rows: [
          ...(targetMetricsApplicable
            ? [
                {
                  label: 'Planlagt',
                  values: tertialBreakdownRows.map((row) => row.samplesPlanned)
                }
              ]
            : []),
          {
            label: 'Tatt',
            values: tertialBreakdownRows.map((row) => row.samplesTaken)
          }
        ]
      }),
    [targetMetricsApplicable, tertialBreakdownRows]
  )

  const paddedTertialBreakdownData = React.useMemo(
    () => buildPaddedChartData(paddedTertialBreakdownRows, 'Tertial'),
    [paddedTertialBreakdownRows]
  )

  const paddedPerTypeResultRows = React.useMemo(
    () =>
      padChartColumns({
        labels: perTypeResult.map((row) => row.label),
        rows: [
          {
            label: 'Ikke påvist',
            values: perTypeResult.map((row) => row.negativeSamples)
          },
          {
            label: 'Påvist',
            values: perTypeResult.map((row) => row.positiveSamples)
          }
        ]
      }),
    [perTypeResult]
  )

  const paddedPerTypeResultData = React.useMemo(
    () => buildPaddedChartData(paddedPerTypeResultRows, 'Prøvetyper'),
    [paddedPerTypeResultRows]
  )

  const paddedSampleDurationRows = React.useMemo(
    () =>
      padChartColumns({
        labels:
          sortedSampleDurationStat?.map((row) =>
            row.name && row.name.trim() !== ''
              ? row.name.trim()
              : sliceId(row.id)
          ) ?? [],
        rows: [
          {
            label: 'Registrert',
            values:
              sortedSampleDurationStat?.map((row) => row.sampleTaken) ?? []
          },
          {
            label: 'Sendt',
            values: sortedSampleDurationStat?.map((row) => row.sampleSent) ?? []
          },
          {
            label: 'Mottatt lab',
            values:
              sortedSampleDurationStat?.map((row) => row.sampleReceived) ?? []
          },
          {
            label: 'Analyse startet',
            values:
              sortedSampleDurationStat?.map((row) => row.analysisStarted) ?? []
          },
          {
            label: 'Klar til behandling',
            values:
              sortedSampleDurationStat?.map((row) => row.readyToProcess) ?? []
          },
          {
            label: 'Kansellert',
            values:
              sortedSampleDurationStat?.map((row) => row.compromised) ?? []
          }
        ],
        // The horizontal bar-stacked chart reads naturally without spacer
        // padding, and padding would push real units off the vertical axis.
        minColumnCount: 0
      }),
    [sortedSampleDurationStat]
  )

  const paddedSampleDurationData = React.useMemo(
    () => buildPaddedChartData(paddedSampleDurationRows, 'Avdeling'),
    [paddedSampleDurationRows]
  )

  const statisticsExportSummaryRows = React.useMemo(
    () =>
      buildStatisticsExportSummaryRows({
        monthBreakdownRows,
        ownerBreakdownRows,
        tertialBreakdownRows,
        planBreakdownRows,
        perTypeResult,
        previousMonthStatus,
        samplesToTakePlan,
        samplesTakenPlan,
        targetMetricsApplicable,
        currentMonthData
      }),
    [
      monthBreakdownRows,
      ownerBreakdownRows,
      perTypeResult,
      planBreakdownRows,
      tertialBreakdownRows,
      previousMonthStatus,
      samplesToTakePlan,
      samplesTakenPlan,
      targetMetricsApplicable,
      currentMonthData
    ]
  )

  useHideChartSpacerBars({
    chartRef: monthBreakdownChartRef,
    spacerIndexes: paddedMonthBreakdownRows.spacerIndexes
  })
  useChartBarValueLabels({
    chartRef: monthBreakdownChartRef,
    rows: paddedMonthBreakdownRows.rows,
    spacerIndexes: paddedMonthBreakdownRows.spacerIndexes
  })
  useHideChartSpacerBars({
    chartRef: tertialBreakdownChartRef,
    spacerIndexes: paddedTertialBreakdownRows.spacerIndexes
  })
  useChartBarValueLabels({
    chartRef: tertialBreakdownChartRef,
    rows: paddedTertialBreakdownRows.rows,
    spacerIndexes: paddedTertialBreakdownRows.spacerIndexes
  })
  useHideChartSpacerBars({
    chartRef: perTypeResultChartRef,
    spacerIndexes: paddedPerTypeResultRows.spacerIndexes
  })

  React.useEffect(() => {
    if (!isSampleExportRequested) return
    if (isError) {
      Mtds.toast.danger('Kunne ikke hente prøver for eksport')
      queueMicrotask(() => setIsSampleExportRequested(false))
      return
    }
    if (!areAllSamplesLoaded) return

    if (!selectedYearSamples.length) {
      Mtds.toast.danger('Ingen prøver å eksportere')
    } else {
      createJsonForDownload(
        selectedYearSamples.map(shapeSampleForStatisticsExport),
        generateExportFilename(
          'prover_' + exportFilterPartsForFilename.filter(Boolean).join('_'),
          'json'
        )
      )
    }
    queueMicrotask(() => setIsSampleExportRequested(false))
  }, [
    areAllSamplesLoaded,
    exportFilterPartsForFilename,
    isError,
    isSampleExportRequested,
    selectedYearSamples
  ])

  const onExportSamplesClick = () => setIsSampleExportRequested(true)

  const onExportStatisticsExcelClick = () => {
    if (statisticsExportSummaryRows.length === 0) {
      Mtds.toast.danger('Ingen statistikk å eksportere')
      return
    }

    createCsvForDownload(
      statisticsExportSummaryRows,
      generateExportFilename(
        'statistikk_' + exportFilterPartsForFilename.filter(Boolean).join('_'),
        'csv'
      )
    )
  }

  const onExportStatisticsPdfClick = () => {
    if (statisticsExportSummaryRows.length === 0) {
      Mtds.toast.danger('Ingen statistikk å eksportere')
      return
    }

    createStatisticsPdfForDownload({
      title: 'Statistikk prøveplan',
      filters: exportFilterParts.filter((part): part is string =>
        Boolean(part)
      ),
      rows: statisticsExportSummaryRows,
      filename: generateExportFilename(
        'statistikk_' + exportFilterPartsForFilename.filter(Boolean).join('_'),
        'pdf'
      )
    })
  }

  const onExportPlansClick = async () => {
    try {
      const fullPlans = await getPlans(authFetch)
      const filteredPlansForExport = filterPlansForExport({
        plans: fullPlans?.plans,
        year: selectedYear,
        selectedPlan,
        allowedOwnerIds: selectedPlanOwnerIds
      })
      if (filteredPlansForExport.length === 0) {
        Mtds.toast.danger('Ingen planer å eksportere')
        return
      }
      const dataRemoved = shapePlansForStatisticsExport(filteredPlansForExport)
      createJsonForDownload(
        dataRemoved,
        generateExportFilename(
          'planer_' + exportFilterPartsForFilename.filter(Boolean).join('_'),
          'json'
        )
      )
    } catch {
      Mtds.toast.danger('Kunne ikke hente planer for eksport')
    }
  }

  const onExportSelectedPlan = async () => {
    if (!selectedPlan) {
      Mtds.toast.danger('Ingen plan å eksportere, velg plan først')
      return
    }
    try {
      const plan = await getPlanById(authFetch, selectedPlan)
      if (!plan) {
        Mtds.toast.danger('Fant ikke valgt plan')
        return
      }
      const dataRemoved = shapeSelectedPlanForExport(plan)
      createJsonForDownload(
        dataRemoved,
        generateExportFilename(
          getFilenameSafePart(plan.description ?? 'plan'),
          'json'
        )
      )
    } catch {
      Mtds.toast.danger('Kunne ikke hente valgt plan for eksport')
    }
  }

  const popId = useId()

  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      topBar={
        <TopBar
          logo={<AppLogo />}
          title='Statistikk'
          showNotificationBell={true}
        />
      }
      isLoading={isLoading}
      loadingMessage='Laster inn prøver...'
      loadingProgress={progress}
      isError={statisticsError}
      isFullWidth
    >
      <ScreenHeader
        title={
          <>
            Statistikk
            <Mtds.Tag data-color='warning'>Under utvikling</Mtds.Tag>
          </>
        }
        actions={
          <Mtds.Button data-tooltip='Eksporter til JSON' popoverTarget={popId}>
            <ExportIcon />
            Eksport
          </Mtds.Button>
        }
      />
      <Mtds.Popover as='menu' id={popId}>
        <li>
          <Mtds.Button onClick={onExportStatisticsExcelClick}>
            <FileTextIcon weight={'thin'} /> Statistikk til Excel
          </Mtds.Button>
        </li>
        <li>
          <Mtds.Button onClick={onExportStatisticsPdfClick}>
            <FileTextIcon weight={'thin'} /> Statistikk til PDF
          </Mtds.Button>
        </li>
        <li>
          <Mtds.Button onClick={onExportSamplesClick}>
            <TestTubeIcon weight={'thin'} /> Filtrerte prøver
          </Mtds.Button>
        </li>
        <li>
          <Mtds.Button onClick={onExportPlansClick} disabled={isPendingPlans}>
            <FileTextIcon weight={'thin'} />
            Filtrerte planer
          </Mtds.Button>
        </li>
        <li>
          <Mtds.Button onClick={onExportSelectedPlan} disabled={!selectedPlan}>
            <FileMinusIcon weight={'thin'} />
            Valgt plan
          </Mtds.Button>
        </li>
      </Mtds.Popover>
      <Mtds.Grid data-gap={6} className='w-full'>
        <StatisticsFilters
          selectedYear={selectedYear}
          yearOptions={yearOptions}
          selectedPlan={selectedPlan}
          regionOptions={regionOptions}
          selectedRegionId={selectedRegionId}
          avdelingOptions={avdelingOptions}
          selectedAvdelingId={selectedAvdelingId}
          seksjonOptions={seksjonOptions}
          selectedSeksjonId={selectedSeksjonId}
          teamOptions={teamOptions}
          selectedTeamId={selectedTeamId}
          selectedEftaNumber={effectiveEftaCode}
          eftaNumberOptions={eftaCodeOptions}
          isLoadingPlans={isPendingPlans}
          planOptions={planOptions}
          currentYear={currentYear}
          resetOrgFilters={resetOrgFilters}
          setSelectedYear={setSelectedYear}
          setSelectedRegionId={setSelectedRegionId}
          setSelectedAvdelingId={setSelectedAvdelingId}
          setSelectedSeksjonId={setSelectedSeksjonId}
          setSelectedTeamId={setSelectedTeamId}
          setSelectedEftaCode={setSelectedEftaCode}
          onSelectedPlanChange={onSelectedPlanChange}
        />
        {isSelected(effectiveEftaCode) && (
          <Mtds.Alert data-color='info'>
            EFTA-filteret avgrenser prøver tatt. Planlagt, gjenstår og
            måloppnåelse vises ikke, fordi planlagte prøveuttak ikke er fordelt
            på slakteri eller EFTA-nummer.
          </Mtds.Alert>
        )}
        {selectedPlan != null &&
          !hasEftaCodeFilter &&
          !targetMetricsApplicable && (
            <Mtds.Alert data-color='info'>
              {selectedPlanTargetYear != null
                ? `Planens mål gjelder for ${selectedPlanTargetYear}. For ${selectedYear} vises bare prøver tatt.`
                : 'Planen mangler målår. For valgt år vises bare prøver tatt.'}
            </Mtds.Alert>
          )}
        <Mtds.Group as={Mtds.Grid} data-gap={5}>
          <Mtds.Flex data-align='center' data-gap={2} className='flex-wrap'>
            <Mtds.Heading data-size='md'>Planoppnåelse</Mtds.Heading>
            <Mtds.HelpText aria-label='Mer informasjon om planoppnåelse'>
              Planlagte prøveuttak og prøver tatt for valgt år og filter. For
              oppgaver som teller delprøver, telles antall delprøver.
            </Mtds.HelpText>
          </Mtds.Flex>
          <Mtds.Grid className='sm:grid-cols-12'>
            <Mtds.Heading data-size='sm' className='sm:col-span-12'>
              {getSummaryMetricTitle(isCurrentReportingYear, selectedYear)}
            </Mtds.Heading>
            <SummaryMetricCardItems
              className='sm:col-span-6 xl:col-span-3'
              samplesToTakePlan={samplesToTakePlan}
              samplesTakenPlan={samplesTakenPlan}
              completionPercent={completionPercent}
              targetMetricsApplicable={targetMetricsApplicable}
              targetYear={selectedPlanTargetYear}
              unavailableHelpText={targetMetricsUnavailableHelpText}
            />
            {((targetMetricsApplicable
              ? hasCumulativeYearlyData
              : cumulativeChartRows.some(
                  (row) => (row.cumulativeTaken ?? 0) > 0
                )) ||
              hasRelevantPlannedTakenData(monthBreakdownRows) ||
              hasRelevantPlannedTakenData(tertialBreakdownRows)) && (
              <>
                <ChartCard
                  title='Akkumulert'
                  className={`${styles.progressCard} h-full sm:col-span-12 xl:col-span-4`}
                >
                  {hasCumulativeYearlyData ? (
                    <ChartContainer
                      categoryCount={cumulativeChartRows.length}
                      className={styles.progressContainer}
                    >
                      <CumulativeChart
                        rows={cumulativeChartRows}
                        year={selectedYear}
                        showPlanned={targetMetricsApplicable}
                        plannedEndpoint={
                          sampleTakingReportQuery.data?.plannedEndpoint
                        }
                      />
                    </ChartContainer>
                  ) : (
                    <Mtds.Alert data-color='warning' className='mt-4'>
                      Ingen data for valgt år og filter.
                    </Mtds.Alert>
                  )}
                </ChartCard>

                {hasRelevantPlannedTakenData(monthBreakdownRows) && (
                  <ChartCard
                    title='Per måned'
                    className={`${styles.progressCard} sm:col-span-12 xl:col-span-4`}
                  >
                    <ChartContainer
                      categoryCount={paddedMonthBreakdownRows.labels.length}
                      className={styles.progressContainer}
                    >
                      <Mtds.Chart
                        aria-label={`Antall prøveuttak per måned for ${selectedYear}`}
                        ref={monthBreakdownChartRef}
                        data-variant='column'
                        className='w-full overview-plan-breakdown-chart'
                        data={paddedMonthBreakdownData}
                      />
                    </ChartContainer>
                  </ChartCard>
                )}

                {hasRelevantPlannedTakenData(tertialBreakdownRows) && (
                  <ChartCard
                    title='Per tertial'
                    className={`${styles.progressCard} sm:col-span-12 xl:col-span-4`}
                  >
                    <ChartContainer
                      categoryCount={paddedTertialBreakdownRows.labels.length}
                      className={styles.progressContainer}
                    >
                      <Mtds.Chart
                        aria-label={`Totalt antall prøveuttak per tertial for ${selectedYear}`}
                        ref={tertialBreakdownChartRef}
                        data-variant='column'
                        className={getColumnChartClass()}
                        data={paddedTertialBreakdownData}
                      />
                    </ChartContainer>
                  </ChartCard>
                )}
              </>
            )}
          </Mtds.Grid>

          <CumulativeStatusCard
            isCurrentReportingYear={isCurrentReportingYear}
            previousMonthStatus={previousMonthStatus}
            currentMonthData={currentMonthData}
            targetMetricsApplicable={targetMetricsApplicable}
          />

          {(hasRelevantPlannedTakenData(visibleOwnerBreakdownRows) ||
            (selectedPlan === undefined &&
              hasRelevantPlannedTakenData(visiblePlanBreakdownRows))) && (
            <Mtds.Grid>
              <Mtds.Heading data-size='sm'>Fordeling</Mtds.Heading>
              {hasRelevantPlannedTakenData(visibleOwnerBreakdownRows) && (
                <ChartCard
                  title={`Prøveuttak fordelt på ${ownerBreakdownLabel}`}
                >
                  <BreakdownTable
                    rows={visibleOwnerBreakdownRows.map((row) => ({
                      key: row.id,
                      name: row.name,
                      samplesPlanned: row.samplesPlanned,
                      samplesTaken: row.samplesTaken
                    }))}
                    firstColumnLabel={ownerBreakdownLabel ?? 'Enhet'}
                    caption={`Prøveuttak fordelt på ${ownerBreakdownLabel}`}
                    targetMetricsApplicable={targetMetricsApplicable}
                  />
                </ChartCard>
              )}

              {selectedPlan === undefined &&
                hasRelevantPlannedTakenData(visiblePlanBreakdownRows) && (
                  <ChartCard title='Planer'>
                    <BreakdownTable
                      rows={visiblePlanBreakdownRows.map((row) => ({
                        key: row.planId,
                        name: row.planName,
                        samplesPlanned: row.samplesPlanned,
                        samplesTaken: row.samplesTaken,
                        href: `/plans/${row.planId}`
                      }))}
                      firstColumnLabel='Plan'
                      caption='Prøveuttak per plan'
                      targetMetricsApplicable={targetMetricsApplicable}
                    />
                  </ChartCard>
                )}
            </Mtds.Grid>
          )}
        </Mtds.Group>

        <Mtds.Group as={Mtds.Grid} data-gap={5}>
          <Mtds.Flex data-align='center' data-gap={2} className='flex-wrap'>
            <Mtds.Heading data-size='md'>Prøver og funn</Mtds.Heading>
            <Mtds.HelpText aria-label='Mer informasjon om prøver og funn'>
              Resultater og fordeling for valgt år og filter.
            </Mtds.HelpText>
          </Mtds.Flex>
          <ResultMetricCard
            samplesPaavist={samplesPaavist}
            samplesAnalyzed={samplesAnalyzed}
            samplesPaavistPercent={samplesPaavistPercent}
          />
          {hasSamplesTaken && (
            <Mtds.Grid>
              <Mtds.Heading data-size='sm'>Fordeling</Mtds.Heading>
              <Mtds.Grid className='lg:grid-cols-2'>
                <ChartCard
                  title='Funn per produkt'
                  helpText='Antall prøver per produkt, delt på ikke påvist og påvist.'
                >
                  <ChartContainer
                    categoryCount={paddedPerTypeResultRows.labels.length}
                  >
                    <Mtds.Chart
                      aria-label='Antall prøver per produkt, delt på ikke påvist og påvist'
                      ref={perTypeResultChartRef}
                      data-variant='bar-stacked'
                      className='overview-result-chart'
                      data={paddedPerTypeResultData}
                    />
                  </ChartContainer>
                </ChartCard>

                <ChartCard
                  title='Påviste prøver per produkt'
                  helpText='Andel av påviste prøver fordelt på produkt.'
                >
                  {samplesPaavist > 0 ? (
                    <Mtds.Chart
                      aria-label='Andel påviste prøver per produkt'
                      ref={sampleTypeDistributionChartRef}
                      data-variant='doughnut'
                      data={sampleTypeDistributionData}
                    />
                  ) : (
                    <Mtds.Alert data-color='info' className='mt-4'>
                      Ingen påviste prøver for valgt år og filter.
                    </Mtds.Alert>
                  )}
                </ChartCard>
              </Mtds.Grid>
            </Mtds.Grid>
          )}
        </Mtds.Group>

        {selectedPlan === undefined && sampleDurationStat != null && (
          <Mtds.Group as={Mtds.Grid} data-gap={5}>
            <Mtds.Flex data-align='center' data-gap={2} data-wrap>
              <Mtds.Heading data-size='md'>Saksbehandlingstid</Mtds.Heading>
              <Mtds.HelpText aria-label='Mer informasjon om saksbehandlingstid'>
                Gjennomsnittlig antall dager prøver bruker i hvert steg.
                Beregnes for prøver som er fullført. Fordi hvert steg beregnes
                hver for seg, er samlet stegtid omtrentlig.
              </Mtds.HelpText>
            </Mtds.Flex>
            <ChartCard title='Gjennomsnittlig antall dager per steg (alle år)'>
              <Mtds.Muted>Tallene påvirkes ikke av filtrene over.</Mtds.Muted>
              {sortedSampleDurationStat?.length ? (
                <Mtds.Grid className='mt-4' data-gap={3}>
                  <ChartContainer
                    categoryCount={paddedSampleDurationRows.labels.length}
                  >
                    <Mtds.Chart
                      aria-label='Gjennomsnittlig saksbehandlingstid i dager per steg, fordelt på avdeling'
                      ref={sampleDurationChartRef}
                      data-variant='bar-stacked'
                      className='overview-status-duration-chart'
                      data={paddedSampleDurationData}
                    />
                  </ChartContainer>
                  {sampleDurationSummary != null && (
                    <Mtds.Prose data-size='2xs'>
                      Snitt samlet stegtid (sum av gjennomsnittene) er{' '}
                      <strong>
                        {formatDays(sampleDurationSummary.averageLeadTime)}
                      </strong>
                      . Snitt per status på tvers av avdelingene:{' '}
                      {sampleDurationSummary.averagesPerStatus.map(
                        (status, index) => (
                          <React.Fragment key={status.label}>
                            {index > 0 && ', '}
                            {status.label}{' '}
                            <strong>{formatDays(status.average)}</strong>
                          </React.Fragment>
                        )
                      )}
                      .
                    </Mtds.Prose>
                  )}
                </Mtds.Grid>
              ) : (
                <Mtds.Alert data-color='info' className='mt-4'>
                  {sampleDurationStat.length > 0
                    ? 'Ingen aktive avdelinger å vise.'
                    : 'Ingen fullførte prøver ennå. Denne oversikten viser kun prøver som har gått gjennom hele forløpet frem til fullført.'}
                </Mtds.Alert>
              )}
            </ChartCard>
          </Mtds.Group>
        )}
      </Mtds.Grid>
    </AppLayout>
  )
}
